import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api, ApiError } from './api';
import type { Me, ProgramSummary } from './types';
import { Spinner } from '@/components/ui/misc';

export const ME_KEY = ['me'] as const;

/** The signed-in user, or null when signed out. */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await api.get<Me>('/api/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function useSetMe() {
  const qc = useQueryClient();
  return (me: Me | null) => qc.setQueryData(ME_KEY, me);
}

/** Where a member lands: executives on Executive, contributors on My work, BlackWolf on the Tracker. */
export function landingPath(p: ProgramSummary) {
  return `/p/${p.id}/${p.role === 'EXECUTIVE' ? 'executive' : p.role === 'CONTRIBUTOR' ? 'my-work' : 'tracker'}`;
}

export function homePath(me: Me) {
  const first = me.programs[0];
  return first ? landingPath(first) : '/no-program';
}

/** Gate for the app: signed in with MFA complete, otherwise route to the right step. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = useMe();
  const loc = useLocation();
  if (isLoading) return <Spinner className="h-full" />;
  if (!me) {
    const next = loc.pathname === '/' ? '' : `?next=${encodeURIComponent(loc.pathname + loc.search)}`;
    return <Navigate to={`/signin${next}`} replace />;
  }
  if (me.mfa === 'verify') return <Navigate to="/mfa" replace />;
  if (me.mfa === 'enroll') return <Navigate to="/mfa/setup" replace />;
  return <>{children}</>;
}
